import React, {useEffect, useRef, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {FaBars, FaTimes} from 'react-icons/fa';
import ReactCountryFlag from 'react-country-flag';

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const Nav = styled.nav`
    background-color: ${props => props.theme.colors.primary};
    padding: 20px 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: relative;
`;

const NavTitle = styled(Link)`
    color: ${props => props.theme.colors.secondary};
    font-size: 1.8rem;
    margin: 0;
    font-weight: bold;
    cursor: pointer;
    text-decoration: none;

    @media (max-width: 768px) {
        font-size: 1.4rem;
    }
`;

const NavLinks = styled.div`
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
        display: none;
    }
`;

const NavLink = styled(Link)`
    color: ${props => props.theme.colors.background};
    background-color: ${props => props.theme.colors.background}20;
    padding: 8px 16px;
    border-radius: 8px;
    text-decoration: none;
    font-size: 1rem;
    font-weight: 500;
    margin-left: 15px;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
        background-color: ${props => props.theme.colors.secondary};
        color: ${props => props.theme.colors.background};
        transform: scale(1.02);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 768px) {
        padding: 6px 12px;
        font-size: 0.9rem;
        margin-left: 10px;
    }
`;

const LanguageDropdown = styled.div`
    position: relative;
    margin-left: 15px;

    @media (max-width: 768px) {
        margin-left: 0;
        width: 100%;
    }
`;

const LanguageButton = styled.button`
    background-color: ${props => props.theme.colors.background}20;
    border: 0;
    color: ${props => props.theme.colors.background};
    padding: 8px 12px;
    border-radius: 5px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;

    &:hover {
        background-color: ${props => props.theme.colors.secondary};
        color: ${props => props.theme.colors.background};
    }

    @media (max-width: 768px) {
        padding: 10px;
        font-size: 0.9rem;
        justify-content: flex-start;
        width: 100%;
        border: none;
        opacity: 0;
        animation: ${fadeIn} 0.2s ease-out forwards;
        animation-delay: 0.3s;
        svg {
            width: 20px;
            height: 20px;
        }
    }
`;

const DropdownMenu = styled.div`
    position: absolute;
    margin-top: 10px;
    top: 100%;
    right: 0;
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.secondary};
    border-radius: 5px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    display: ${props => (props.isOpen ? 'block' : 'none')};
    z-index: 1000;
    animation: slideDown 0.3s ease-out forwards;
    min-width: 140px;

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @media (max-width: 768px) {
        position: static;
        border: none;
        box-shadow: none;
        background-color: transparent;
    }
`;

const DropdownItem = styled.button`
    background: none;
    border: none;
    color: ${props => props.theme.colors.background};
    padding: 10px 16px;
    width: 100%;
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.95rem;
    transition: background-color 0.2s ease;
    opacity: 0;
    animation: ${fadeIn} 0.2s ease-out forwards;
    animation-delay: ${props => props.index * 0.1}s;

    &:hover {
        background-color: ${props => props.theme.colors.secondary}40;
    }

    @media (max-width: 768px) {
        padding: 10px;
        font-size: 0.9rem;
        color: ${props => props.theme.colors.secondary};
    }
`;

const HamburgerButton = styled.button`
    display: none;
    background: none;
    border: none;
    color: ${props => props.theme.colors.secondary};
    font-size: 1.5rem;
    cursor: pointer;

    @media (max-width: 768px) {
        display: block;
    }
`;

const MobileMenu = styled.div`
    display: ${props => (props.isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: ${props => props.theme.colors.primary};
    border-top: 1px solid ${props => props.theme.colors.secondary};
    padding: 10px;
    z-index: 1000;
    animation: ${fadeIn} 0.3s ease-out forwards;
`;

const MobileNavLink = styled(Link)`
    color: ${props => props.theme.colors.secondary};
    padding: 10px;
    text-decoration: none;
    font-size: 1rem;
    font-weight: 500;
    transition: background-color 0.2s ease;
    opacity: 0;
    animation: ${fadeIn} 0.2s ease-out forwards;
    animation-delay: ${props => props.index * 0.1}s;

    &:hover {
        background-color: ${props => props.theme.colors.secondary}20;
    }
`;

const LANGUAGES = [
    { code: 'it', countryCode: 'IT', label: 'Italiano' },
    { code: 'en', countryCode: 'GB', label: 'English' },
    { code: 'de', countryCode: 'DE', label: 'Deutsch' },
];

const Navbar = ({rentUser, config}) => {
    const {i18n, t} = useTranslation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const dropdownRef = useRef(null);
    const mobileMenuRef = useRef(null);

    // Normalizza la lingua a codice ISO (it, en, de)
    const currentLang = (i18n.resolvedLanguage || i18n.language || 'it').split('-')[0].toLowerCase();
    const activeLang = LANGUAGES.some(l => l.code === currentLang) ? currentLang : 'it';
    const currentLanguageInfo = LANGUAGES.find(l => l.code === activeLang) || LANGUAGES[0];

    useEffect(() => {
        const handleClickOutside = (event) => {
            const inDesktop = dropdownRef.current && dropdownRef.current.contains(event.target);
            const inMobile = mobileMenuRef.current && mobileMenuRef.current.contains(event.target);
            if (!inDesktop && !inMobile) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const toggleDropdown = () => {
        setIsDropdownOpen(prev => !prev);
    };

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        setIsDropdownOpen(false);
        setIsMobileMenuOpen(false);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        if (isDropdownOpen) setIsDropdownOpen(false);
    };

    return (
        <Nav>
            <NavTitle to="/">{rentUser?.name || t('welcome')}</NavTitle>
            <NavLinks>
                <NavLink to="/">{t('home')}</NavLink>
                <NavLink to="/bikes">{t('ourBikes')}</NavLink>
                {config?.isServicesPageAvailable && (
                    <NavLink to="/services">{t('services', {defaultValue: 'Servizi'})}</NavLink>
                )}
                <NavLink to="/contact">{t('contactUs')}</NavLink>
                <LanguageDropdown ref={dropdownRef}>
                    <LanguageButton onClick={toggleDropdown} aria-label={`Lingua: ${currentLanguageInfo.label}`}>
                        <ReactCountryFlag countryCode={currentLanguageInfo.countryCode} svg />
                        {currentLanguageInfo.label}
                    </LanguageButton>
                    <DropdownMenu isOpen={isDropdownOpen}>
                        {LANGUAGES.map((lang, index) => (
                            <DropdownItem key={lang.code} index={index} onClick={() => changeLanguage(lang.code)}>
                                <ReactCountryFlag countryCode={lang.countryCode} svg /> {lang.label}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </LanguageDropdown>
            </NavLinks>
            <HamburgerButton onClick={toggleMobileMenu} aria-label="Menu">
                {isMobileMenuOpen ? <FaTimes/> : <FaBars/>}
            </HamburgerButton>
            <MobileMenu isOpen={isMobileMenuOpen}>
                <MobileNavLink index={0} to="/" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('home')}
                </MobileNavLink>
                <MobileNavLink index={1} to="/bikes" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('ourBikes')}
                </MobileNavLink>
                {config?.isServicesPageAvailable && (
                    <MobileNavLink index={2} to="/services" onClick={() => setIsMobileMenuOpen(false)}>
                        {t('services', {defaultValue: 'Servizi'})}
                    </MobileNavLink>
                )}
                <MobileNavLink index={2} to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('contactUs')}
                </MobileNavLink>
                <LanguageDropdown ref={mobileMenuRef}>
                    <LanguageButton onClick={toggleDropdown} aria-label={`Lingua: ${currentLanguageInfo.label}`}>
                        <ReactCountryFlag countryCode={currentLanguageInfo.countryCode} svg />
                        {currentLanguageInfo.label}
                    </LanguageButton>
                    <DropdownMenu isOpen={isDropdownOpen}>
                        {LANGUAGES.map((lang, index) => (
                            <DropdownItem key={lang.code} index={index} onClick={() => changeLanguage(lang.code)}>
                                <ReactCountryFlag countryCode={lang.countryCode} svg /> {lang.label}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </LanguageDropdown>
            </MobileMenu>
        </Nav>
    );
};

export default Navbar;
